// Per-square expand buttons.
//
// One small button per grid cell holding MORE THAN THREE cards, projected
// over the cell's top-left corner each frame. Clicking it opens the cell's
// mesh fan (openCellFocus). The mouse-only path beside the held expand
// hotkey. Hidden while a prompt owns board clicks (target picks / server
// choices) so the button can never swallow a prompt click (the CLASS 1
// regression the old count badge caused).

import { sampleCellGeometry } from '../../../utils/game/engine/core';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { CELL_STACK_TUTORIAL_KEY } from './eventTutorials';

// The cell a card mesh belongs to. Prefers the authoritative server cell
// on the instance; falls back to resolving the mesh's world position when
// no cell is stamped (pre-position avatars).
export function cellKeyForMesh(host, mesh) {
  const card = mesh?.userData?.cardInstance;
  const cell = card?.cell;
  if (cell && typeof cell.col === 'number' && typeof cell.row === 'number') {
    return `${cell.col},${cell.row}`;
  }
  if (typeof host?._cellAtWorld === 'function' && mesh?.position) {
    const at = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (at) return `${at.col},${at.row}`;
  }
  return null;
}

// Group every in-play card mesh by its cell.
export function groupCardMeshesByCell(host) {
  const groups = new Map();
  for (const [id, mesh] of host.meshes) {
    if (mesh?.userData?.type !== 'card') continue;
    const key = cellKeyForMesh(host, mesh);
    if (!key) continue;
    const card = mesh.userData.cardInstance;
    let arr = groups.get(key);
    if (!arr) groups.set(key, (arr = []));
    arr.push({ id, mesh, card });
  }
  return groups;
}

export function registerCellStackBadgeOverlayRef(host, cellKey, el) {
  if (!el) host.cellStackBadgeOverlayRefs.delete(cellKey);
  else {
    host.cellStackBadgeOverlayRefs.set(cellKey, el);
    // A badge just mounted, so the expand button became visible.
    // First-time trigger for the cell-stack tutorial (one Set lookup
    // after the first call, see eventTutorials).
    host._queueEventTutorial?.(CELL_STACK_TUTORIAL_KEY, { cellKey });
  }
}

// Per-frame projector. Anchors each expand button over its cell's
// top-left corner. Mirrors updateCellElementPipOverlays: project through
// the live camera and write a canvas-local translate.
export function updateCellStackBadgeOverlays(host) {
  if (host.cellStackBadgeOverlayRefs.size === 0) return;
  if (!host.scene?.camera || !host.canvasRef?.current) return;
  const grid = getGameGrid(host.state?.spawnConfig);
  if (!grid) return;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();
  const v = host._hugVec;
  // Same SCREEN corner for both players (mirror of the element pip strips):
  // the viewer's top-left of the square is world tl for P1 and world br for
  // P2's 180°-rotated camera.
  const viewerRotated = host._localCameraRotated?.() ?? false;
  for (const [cellKey, el] of host.cellStackBadgeOverlayRefs) {
    const [colStr, rowStr] = cellKey.split(',');
    const geo = sampleCellGeometry(grid, parseInt(colStr, 10), parseInt(rowStr, 10));
    if (!geo) { el.style.display = 'none'; continue; }
    // Anchor toward the viewer's top-left corner (70% out from center) so
    // the button rides the square's edge instead of covering resting cards.
    const corner = viewerRotated ? geo.br : geo.tl;
    const cx = (geo.tl.x + geo.br.x) / 2;
    const cz = (geo.tl.z + geo.br.z) / 2;
    const ax = cx + (corner.x - cx) * 0.7;
    const az = cz + (corner.z - cz) * 0.7;
    v.set(ax, 0.2, az).project(camera);
    const sx = ((v.x + 1) / 2) * rect.width;
    const sy = ((-v.y + 1) / 2) * rect.height;
    el.style.display = 'flex';
    el.style.transform = `translate(${Math.round(sx)}px, ${Math.round(sy)}px) translate(-50%, -50%)`;
  }
}

// Data for the expand buttons. One entry per cell holding MORE THAN THREE
// cards, excluding the focused cell (its contents are already fanned open).
// Deliberately AVAILABLE during target prompts and combat selection. A
// crowded square is exactly where picking a defender needs the fan. Only
// cell-pick flows suppress it (a click there means "this cell", and the
// button must not sit over it).
export function buildCellStackBadges(host) {
  if (host.state?.pendingCellTarget || host.state?.pendingSummonPlacement
    || host.state?.pendingServerChoice?.kind === 'cell') return [];
  const openKey = host.state?.focusedCell;
  const badges = [];
  for (const [cellKey, arr] of groupCardMeshesByCell(host)) {
    if (arr.length <= 3 || cellKey === openKey) continue;
    const [colStr, rowStr] = cellKey.split(',');
    badges.push({ cellKey, col: parseInt(colStr, 10), row: parseInt(rowStr, 10), count: arr.length });
  }
  return badges;
}
